import {
  Component,
  inject,
  OnInit,
  OnDestroy,
  ViewEncapsulation,
  Input,
} from '@angular/core';
import { CustomTabComponent } from '../../../components/ui/custom-tab/custom-tab.component';
import { Router } from '@angular/router';
import { Test } from '../../../core/interfaces/test';
import { TestService } from '../../../core/services/test.service';
import { StudentTestService } from '../../../core/services/student-test.service';
import { CommonModule } from '@angular/common';
import { LoaderComponent } from '../../../components/ui/loader/loader.component';
import {
  LangChangeEvent,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { SwalService } from '../../../core/services/utils/swal.service';
import { AuthService } from '../../../core/services/utils/auth.service';
import { SyncfusionL10nService } from '../../utils/syncfusion-l10n.service';
import { Subscription, firstValueFrom } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import { LanguageService } from '../../../core/services/utils/language.service';
import { Subject as RxJsSubject } from 'rxjs';

@Component({
  selector: 'app-student-tests',
  standalone: true,
  imports: [CustomTabComponent, CommonModule, LoaderComponent, TranslateModule],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './student-tests.component.html',
  styleUrl: './student-tests.component.css',
})
export class StudentTestsComponent implements OnInit, OnDestroy {
  @Input() testMode: string = 'evaluation'; // Default to 'exam', can be 'exam' or 'evaluation'

  isRTL = false;
  currentLocale = 'ar';
  isLoading = false;
  isSaving = false;
  error: string | null = null;
  public data: Test[] = [];
  hasError = false;
  errorMessage = '';
  public pageSettings!: Object;
  public toolbar!: string[];
  selectedTest: Test | null = null;
  private testsSub?: Subscription;
  private langChangeSub?: Subscription;
  public enableRTL = true;
  testModeTranslations: { [key: string]: string } = {};
  public translatedHeaders = {
    title: '',
    mode: '',
    count: '',
    subject: '',
    attempts: '',
    actions: '',
  };

  /** Colonnes pour la table partagée (via app-custom-tab). */
  public columns: any[] = [];

  private router = inject(Router);
  private testService = inject(TestService);
  private studentTestService = inject(StudentTestService);
  private translate = inject(TranslateService);
  private swal = inject(SwalService);
  private authService = inject(AuthService);
  private syncfusionL10n = inject(SyncfusionL10nService);
  private languageService = inject(LanguageService);
  private studentId: string | null = null;
  private destroy$ = new RxJsSubject<void>();

  async ngOnInit(): Promise<void> {
    // Get student ID from AuthService
    this.studentId = await this.authService.getUserId();

    if (!this.studentId) {
      console.error('❌ Failed to get student ID');
      this.handleError(
        new Error('Unable to retrieve student ID. Please contact support.'),
      );
      return;
    }

    console.log('✅ Student ID loaded:', this.studentId);

    await this.initializeComponent();
  }

  private async initializeComponent(): Promise<void> {
    try {
      // Set initial language
      const lang = this.languageService.getCurrentLang();
      this.setGridLanguage(lang);
      await this.loadTranslations();

      // Subscribe to language changes
      this.langChangeSub = this.translate.onLangChange.subscribe(
        async (event: LangChangeEvent) => {
          this.setGridLanguage(event.lang);
          await this.loadTranslations();
        },
      );

      // Initialize grid settings
      this.initGridSettings();

      // Load student tests by mode
      this.loadStudentTestsByMode();
    } catch (error) {
      console.error('❌ Error initializing component:', error);
      this.handleError(error);
    }
  }

  loadStudentTestsByMode(): void {
    try {
      this.isLoading = true;
      this.hasError = false;
      this.errorMessage = '';

      if (!this.studentId) {
        throw new Error('Student ID is required');
      }

      if (!this.testMode) {
        throw new Error('Test mode is required');
      }

      console.log(
        `📚 Loading ${this.testMode} tests for student:`,
        this.studentId,
      );

      this.studentTestService
        .getTestsByTestMode(this.studentId, this.testMode)
        .pipe(
          takeUntil(this.destroy$),
          tap((response) => {
            console.log('📊 Tests response received:', {
              status: response.status,
              testMode: this.testMode,
              testsCount: response?.data?.length || 0,
              tests: response?.data,
            });
          }),
          catchError((error) => {
            console.error(`❌ Error loading ${this.testMode} tests:`, error);
            this.handleError(error);
            throw error;
          }),
          finalize(() => {
            this.isLoading = false;
          }),
        )
        .subscribe({
          next: (response) => {
            try {
              if (response.status === 'SUCCESS') {
                this.data = response.data || [];
                console.log(
                  `✅ ${this.testMode} tests loaded successfully:`,
                  this.data.length,
                  'Tests:',
                  this.data,
                );
                this.hasError = false;
              } else {
                throw new Error(
                  response.message || `Failed to load ${this.testMode} tests`,
                );
              }
            } catch (error) {
              console.error('❌ Error processing data:', error);
              this.handleError(error);
            }
          },
          error: (error) => {
            console.error('❌ Subscription error:', error);
            this.handleError(error);
          },
        });
    } catch (error) {
      console.error(`❌ Error in loadStudentTestsByMode:`, error);
      this.handleError(error);
    }
  }

  async loadTranslations(): Promise<void> {
    try {
      const translations = await firstValueFrom(
        this.translate.get([
          'DATA_TABLE.TITLE',
          'DATA_TABLE.MODE',
          'DATA_TABLE.QUESTION_COUNT',
          'DATA_TABLE.SUBJECT',
          'DATA_TABLE.ACTIONS',
          'TESTS.ATTEMPTS',
          'TEST_MODE.EXAM',
          'TEST_MODE.PRACTICE',
          'TEST_MODE.EVALUATION',
        ]),
      );

      this.translatedHeaders = {
        title: translations['DATA_TABLE.TITLE'],
        mode: translations['DATA_TABLE.MODE'],
        count: translations['DATA_TABLE.QUESTION_COUNT'],
        subject: translations['DATA_TABLE.SUBJECT'],
        attempts: translations['TESTS.ATTEMPTS'],
        actions: translations['DATA_TABLE.ACTIONS'],
      };

      this.testModeTranslations = {
        exam: translations['TEST_MODE.EXAM'],
        practice: translations['TEST_MODE.PRACTICE'],
        evaluation: translations['TEST_MODE.EVALUATION'],
      };
      this.buildColumns();
    } catch (error) {
      console.error('❌ Error loading translations:', error);
      // Set default values if translations fail
      this.translatedHeaders = {
        title: 'Title',
        mode: 'Mode',
        count: 'Questions',
        subject: 'Subject',
        attempts: 'Attempts',
        actions: 'Actions',
      };
      this.buildColumns();
    }
  }

  /** Colonnes de la table partagée ; les cellules riches sont fournies en
   *  gabarits (mode, nombre, tentatives) via `cellTemplates`. */
  private buildColumns(): void {
    const h = this.translatedHeaders;
    this.columns = [
      { key: 'title', field: 'title', headerText: h.title },
      { key: 'testMode', field: 'testMode', headerText: h.mode },
      { key: 'questionsCount', field: 'questionsCount', headerText: h.count, textAlign: 'Center' },
      { key: 'subjectName', field: 'subjectName', headerText: h.subject },
      { key: 'attempts', headerText: h.attempts, textAlign: 'Center' },
      { key: 'actions', headerText: h.actions, template: 'actionTemplate', textAlign: 'Center' },
    ];
  }

  setGridLanguage(lang: string): void {
    this.currentLocale = lang;
    this.enableRTL = lang === 'ar';
  }

  startOrResume(test: Test): void {
    if (test.studentSummary?.hasActiveSession && test.studentSummary.activeSessionId) {
      this.router.navigate(['/student/test/take', test.id], {
        queryParams: { sessionId: test.studentSummary.activeSessionId },
      });
    } else {
      this.launchTest(test.id);
    }
  }

  launchTest(testId: number): void {
    if (!testId) {
      console.error('❌ Invalid test ID');
      return;
    }
    this.router.navigate(['/student/test/take', testId]);
  }

  private initGridSettings(): void {
    this.pageSettings = {
      pageCount: 5,
      pageSize: 10,
      pageSizes: [10, 20, 50, 100],
      enableQueryString: false,
      template: null,
    };
    this.toolbar = ['Search'];
  }

  translateTestMode(testMode: string): string {
    if (!testMode) return '—';
    return this.testModeTranslations[testMode] || testMode;
  }

  private handleError(error: any): void {
    this.isLoading = false;
    this.hasError = true;
    this.errorMessage =
      error?.message || 'An error occurred while loading data';
    console.error('❌ Component error:', error);

    // Show error to user
    const errorMsg = this.translate.instant('SWAL.LOAD_DATA_ERROR');
    this.swal.error(errorMsg);
  }

  retryLoad(): void {
    console.log('🔄 Retrying data load...');
    this.loadStudentTestsByMode();
  }

  ngOnDestroy(): void {
    this.testsSub?.unsubscribe();
    this.langChangeSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    console.log('🧹 Component destroyed and subscriptions cleaned up');
  }
}
